/**
 * Phase 45 Plan 45-05 — Portfolio table row.
 *
 * 7-column grid per UI-SPEC §Surface 1 Column grid:
 *   Project · Status · Burn vs plan · Revenue · Margin · Team · Due
 *
 * Row backgrounds:
 *   - default:    hover:bg-ms-surface-2/40
 *   - overBudget: bg-red-50/30 (burnPct > 1) per UI-SPEC §Portfolio table row hover state
 *
 * A11y: over-budget rows carry `aria-describedby` linking to a visually
 * hidden span "Over budget by {N}%" so screen readers announce the visual
 * red tint (UI-SPEC §A11y Contract).
 */
import { useId } from 'react';
import { Link } from 'react-router-dom';
import { StatusPill } from './StatusPill';
import { BurnBar } from './BurnBar';
import { DueLabel } from './DueLabel';
import { TeamAvatars } from './TeamAvatars';
import { formatMargin, formatPortfolioMoney, marginToneClass } from './money';
import { portfolioGridClass } from './portfolioGrid';
import type { PortfolioRow as PortfolioRowData } from '../../services/api/portfolio';

export interface PortfolioRowProps {
  row: PortfolioRowData;
  /**
   * MKS-332 — false when the API omitted cost and margin (the viewer is
   * neither admin nor finance). Passed down from the table rather than
   * derived per row, so every row agrees with the header's column count.
   */
  showMargin?: boolean;
}

/**
 * MKS-328 — the zero case keeps its own literal so a zero-revenue row stays a
 * short, unmistakable "0 Kč".
 */
function formatRevenue(value: number, billable: boolean): string {
  if (!billable) return '—';
  if (value === 0) return '0 Kč';
  return formatPortfolioMoney(value);
}

function customerDotColor(row: PortfolioRowData): string {
  if (row.customerColorHex) return row.customerColorHex;
  // Derive a deterministic palette index from customerId when no hex supplied.
  const palette = ['#D2691E', '#0EA5E9', '#10B981', '#F59E0B', '#A78BFA', '#F43F5E'];
  const id = row.customerId ?? row.id;
  return palette[id % palette.length] ?? '#D2691E';
}

export function PortfolioRow({ row, showMargin = true }: PortfolioRowProps) {
  const overBudget = row.burnPct > 1;
  const describedById = useId();
  const describedBy = overBudget ? describedById : undefined;

  const rowClass = `${portfolioGridClass(showMargin)} items-center border-b border-ms-border-subtle ${
    overBudget ? 'bg-red-50/30' : ''
  } hover:bg-ms-surface-2/40 focus-within:bg-ms-surface-2/40`;

  return (
    <div className={rowClass} aria-describedby={describedBy}>
      {overBudget && (
        <span id={describedById} className="sr-only">
          Over budget by {Math.round((row.burnPct - 1) * 100)} percent
        </span>
      )}

      {/* Col 1 — Project */}
      <div className="px-4 py-2.5 flex items-start gap-2 min-w-0">
        <span
          className="mt-1 w-2 h-2 rounded-full shrink-0"
          style={{ background: customerDotColor(row) }}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <Link
            to={`/projects/${row.id}`}
            className="text-[12.5px] font-medium text-ms-text-strong truncate hover:text-ms-orange-dark focus:outline-none focus:ring-2 focus:ring-ms-orange/40 rounded-sm block"
            title={row.fullName ?? row.name}
          >
            {row.fullName ?? row.name}
          </Link>
          <div className="text-[10.5px] text-ms-text-muted truncate">
            {row.customerName ?? 'No customer'}
            {row.milestoneLabel && ` · ${row.milestoneLabel}`}
          </div>
        </div>
      </div>

      {/* Col 2 — Status */}
      <div className="px-2 py-2.5">
        <StatusPill status={row.status} />
      </div>

      {/* Col 3 — Burn vs plan */}
      <div className="px-2 py-2.5">
        <BurnBar pct={row.burnPct} showLabel />
      </div>

      {/* Col 4 — Revenue */}
      <div className="px-2 py-2.5 text-right text-[11.5px] tabular-nums text-ms-text-strong">
        {formatRevenue(row.revenue, row.billable)}
      </div>

      {/* Col 5 — Margin. MKS-332: absent, not dashed, without cost access. */}
      {showMargin && (
        <div
          className={`px-2 py-2.5 text-right text-[11.5px] tabular-nums font-medium ${marginToneClass(
            row.margin ?? null,
          )}`}
        >
          {formatMargin(row.margin ?? null)}
        </div>
      )}

      {/* Col 6 — Team */}
      <div className="px-2 py-2.5">
        <TeamAvatars team={row.team} />
      </div>

      {/* Col 7 — Due */}
      <div className="px-4 py-2.5">
        <DueLabel dueDays={row.dueDays} />
      </div>
    </div>
  );
}
